import { useMemo } from 'react';
import { Typography } from '@material-ui/core';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import EventNoteOutlinedIcon from '@material-ui/icons/EventNoteOutlined';
import {
  VerticalTabNav,
  TabItemData,
} from '@openchoreo/backstage-design-system';
import {
  formatRelativeTime,
  useUrlSyncedTab,
  DetailPageLayout,
} from '@openchoreo/backstage-plugin-react';
import type { RunDetailsTab } from '@openchoreo/backstage-plugin-react';
import { BuildStatusChip } from '../BuildStatusChip';
import { LogsContent } from '../BuildLogs';
import { EventsContent } from '../BuildEvents';
import { RunMetadataContent } from '../RunMetadataContent';
import type { ModelsBuild } from '@openchoreo/backstage-plugin-common';
import type { GitFieldMapping } from '../../utils/schemaExtensions';

interface WorkflowRunDetailsPageProps {
  run: ModelsBuild;
  onBack: () => void;
  /** Initial tab to display (from URL) */
  initialTab?: RunDetailsTab;
  /** Callback when tab changes (to update URL) */
  onTabChange?: (tab: RunDetailsTab) => void;
  gitFieldMapping?: GitFieldMapping;
}

export const WorkflowRunDetailsPage = ({
  run,
  onBack,
  initialTab,
  onTabChange,
  gitFieldMapping,
}: WorkflowRunDetailsPageProps) => {
  const [activeTab, setActiveTab] = useUrlSyncedTab<RunDetailsTab>({
    initialTab,
    defaultTab: 'logs',
    onTabChange,
  });

  const tabs = useMemo<TabItemData[]>(
    () => [
      {
        id: 'logs',
        label: 'Logs',
        icon: <DescriptionOutlinedIcon fontSize="small" />,
      },
      {
        id: 'events',
        label: 'Events',
        icon: <EventNoteOutlinedIcon fontSize="small" />,
      },
      {
        id: 'details',
        label: 'Details',
        icon: <InfoOutlinedIcon fontSize="small" />,
      },
    ],
    [],
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'logs':
        return <LogsContent build={run} />;
      case 'events':
        return <EventsContent build={run} />;
      case 'details':
        return (
          <RunMetadataContent build={run} gitFieldMapping={gitFieldMapping} />
        );
      default:
        return null;
    }
  };

  const subtitle = (
    <>
      <BuildStatusChip status={run.status} />
      <Typography variant="body2" color="textSecondary">
        {formatRelativeTime(run.createdAt || '')}
      </Typography>
    </>
  );

  return (
    <DetailPageLayout
      title={run.name || 'Workflow Run'}
      subtitle={subtitle}
      onBack={onBack}
    >
      <VerticalTabNav
        tabs={tabs}
        activeTabId={activeTab}
        onChange={tabId => setActiveTab(tabId as RunDetailsTab)}
      >
        {renderTabContent()}
      </VerticalTabNav>
    </DetailPageLayout>
  );
};
